import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '@/hooks/effects/use-debounce';
import { odooApiService } from '@/services/odoo.service';
import type { OdooResourceType, OdooSearchItem, UnlinkedOdooResource } from '@/types/odoo.types';

const MIN_SEARCH_LENGTH = 2;
const DEBOUNCE_MS = 300;

interface UseOdooUnlinkedOptions {
  /** The Odoo entity type to fetch unlinked records for. */
  resourceType: OdooResourceType;
  /** Whether the hook is enabled. */
  enabled?: boolean;
}

/**
 * Hook that provides debounced search against the Odoo "unlinked" endpoint.
 * Returns only Odoo records that are NOT already linked to a Hub resource.
 * Only triggers a request when the search term has >= 2 characters.
 */
export function useOdooUnlinked({ resourceType, enabled = true }: UseOdooUnlinkedOptions) {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedTerm = useDebounce(searchTerm, DEBOUNCE_MS);

  const shouldSearch = enabled && debouncedTerm.length >= MIN_SEARCH_LENGTH;

  const { data, isLoading, isFetching, error } = useQuery<UnlinkedOdooResource>({
    queryKey: ['odoo-unlinked', resourceType, debouncedTerm],
    queryFn: () => odooApiService.getUnlinked(resourceType, debouncedTerm),
    enabled: shouldSearch,
    staleTime: 30 * 1000, // 30 seconds
    keepPreviousData: true,
  });

  return {
    /** Current search term. */
    searchTerm,
    /** Update the search input value. */
    setSearchTerm,
    /** Unlinked Odoo records matching the search. */
    results: (data?.items ?? []) as OdooSearchItem[],
    /** Total number of unlinked records matching the search. */
    total: data?.total ?? 0,
    /** True while the initial request is loading. */
    isLoading: shouldSearch && isLoading,
    /** True while any fetch is in progress (including background refetches). */
    isFetching: shouldSearch && isFetching,
    /** Error from the request, if any. */
    error,
  };
}
